/*
 * reveal.ts — scroll-reveal for .reveal elements and .divider rules.
 *
 * No-JS resting state (everything visible) lives in site.css via
 * `html:not(.js) .reveal`; reduced-motion and missing IntersectionObserver both
 * short-circuit to "just show it". Each element fires once, then is unobserved.
 */

export function initReveal(): void {
  const revealEls = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
  const dividers = Array.from(document.querySelectorAll<HTMLElement>(".divider"));
  if (!revealEls.length && !dividers.length) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion || !("IntersectionObserver" in window)) {
    revealEls.forEach((el) => el.classList.add("is-visible"));
    dividers.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  // Stagger siblings within the same parent.
  const seen = new Map<Element, number>();
  revealEls.forEach((el) => {
    const parent = el.parentElement ?? document.body;
    const i = seen.get(parent) ?? 0;
    el.style.setProperty("--reveal-i", String(i));
    seen.set(parent, i + 1);
  });

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
  );

  revealEls.forEach((el) => observer.observe(el));
  dividers.forEach((el) => observer.observe(el));
}
